import React, { useState } from 'react';
import { Card, Button, Form, Row, Col, Modal, Alert } from 'react-bootstrap';
import { FaPlus, FaKey } from 'react-icons/fa';
import CourseCodesStats from './CourseCodesStats';
import CourseCodesList from './CourseCodesList';
import { generateCourseCodes } from '../../services/courseService';
import { Course } from '../../types';

interface CourseCodesManagerProps {
  course: Course;
}

const CourseCodesManager: React.FC<CourseCodesManagerProps> = ({ course }) => {
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [count, setCount] = useState(1);
  const [expiryDays, setExpiryDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleGenerateCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // التحقق من المدخلات
      if (count < 1 || count > 100) {
        throw new Error('عدد الأكواد يجب أن يكون بين 1 و 100');
      }
      if (expiryDays < 1 || expiryDays > 365) {
        throw new Error('مدة صلاحية الأكواد يجب أن تكون بين 1 و 365 يوم');
      }

      // توليد الأكواد
      const result = await generateCourseCodes(course.id, count, expiryDays);
      setSuccess(`تم إنشاء ${count} كود بنجاح`);
      setShowGenerateModal(false);
      setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error('Error generating codes:', error);
      setError(error instanceof Error ? error.message : 'حدث خطأ أثناء إنشاء الأكواد');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="course-codes-manager">
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaKey className="me-2" />
              إدارة أكواد التفعيل
            </h5>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowGenerateModal(true)}
            >
              <FaPlus className="me-1" />
              إنشاء أكواد جديدة
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
              {success}
            </Alert>
          )}

          <CourseCodesStats courseId={course.id} />
          
          <div className="mt-4">
            <CourseCodesList
              course={course}
              refreshTrigger={refreshTrigger}
            />
          </div>
        </Card.Body>
      </Card>

      {/* Modal إنشاء أكواد جديدة */}
      <Modal show={showGenerateModal} onHide={() => setShowGenerateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>إنشاء أكواد تفعيل جديدة</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>عدد الأكواد</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={100}
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value))}
              />
              <Form.Text className="text-muted">
                يمكنك إنشاء من 1 إلى 100 كود في المرة الواحدة
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>مدة الصلاحية (بالأيام)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={365}
                value={expiryDays}
                onChange={(e) => setExpiryDays(parseInt(e.target.value))}
              />
              <Form.Text className="text-muted">
                مدة صلاحية الأكواد من 1 إلى 365 يوم
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateModal(false)}>
            إلغاء
          </Button>
          <Button
            variant="primary"
            onClick={handleGenerateCodes}
            disabled={loading}
          >
            {loading ? 'جاري الإنشاء...' : 'إنشاء الأكواد'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseCodesManager;
